import React, { useState } from "react";
import { motion } from "motion/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import FileDisplay from "../files/FileDisplay";
import { File } from "@/types/breeze/File";
import ImageGalleryOverlay from "../ui/ImageGalleryOverlay";

export interface ImageFile extends File {
  url?: string;
  [key: string]: unknown;
}

interface ImageCarouselProps {
  /**
   * Array of files to display in the carousel
   */
  files: ImageFile[];

  /**
   * Custom class name for the container
   */
  className?: string;

  /**
   * Whether to show navigation controls
   * @default true
   */
  showControls?: boolean;

  /**
   * Whether to show the counter indicator
   * @default true
   */
  showCounter?: boolean;

  /**
   * Initial image index to display
   * @default 0
   */
  initialIndex?: number;

  /**
   * Function called when an image is clicked
   */
  onImageClick?: (
    file: ImageFile,
    index: number,
    event: React.MouseEvent,
  ) => void;

  /**
   * Whether to enable the built-in gallery overlay
   * @default true
   */
  enableGallery?: boolean;
}

/**
 * A reusable image carousel component
 */
const ImageCarousel: React.FC<ImageCarouselProps> = ({
  files,
  className = "",
  showControls = true,
  showCounter = true,
  initialIndex = 0,
  onImageClick,
  enableGallery = true,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [galleryOpen, setGalleryOpen] = useState(false);

  // Skip rendering if no files
  if (!files || files.length === 0) return null;

  // Ensure initial index is valid
  const validIndex = Math.min(Math.max(0, initialIndex), files.length - 1);
  if (validIndex !== initialIndex) {
    setCurrentIndex(validIndex);
  }

  const navigatePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : files.length - 1));
  };

  const navigateNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < files.length - 1 ? prev + 1 : 0));
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (enableGallery) {
      setGalleryOpen(true);
    } else if (onImageClick) {
      onImageClick(files[currentIndex], currentIndex, e);
    }
  };

  // When the gallery closes, make sure we handle it properly
  const handleGalleryClose = () => {
    setGalleryOpen(false);
  };

  return (
    <>
      <div
        className={`relative w-full overflow-hidden rounded-md ${className}`}
      >
        <motion.div
          className="w-full h-full"
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          onClick={handleImageClick}
        >
          <FileDisplay files={[files[currentIndex]]} insideCarousel={true} />
        </motion.div>

        {showControls && files.length > 1 && (
          <>
            <button
              onClick={navigatePrev}
              className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-white/70 dark:bg-gray-800/70 p-1 rounded-full text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800"
              aria-label="Previous image"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={navigateNext}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-white/70 dark:bg-gray-800/70 p-1 rounded-full text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800"
              aria-label="Next image"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}

        {showCounter && files.length > 1 && (
          <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 text-xs bg-white/70 dark:bg-gray-800/70 px-2 py-0.5 rounded-full text-gray-600 dark:text-gray-300">
            {currentIndex + 1} / {files.length}
          </div>
        )}
      </div>

      {galleryOpen && (
        <ImageGalleryOverlay
          files={files}
          initialIndex={currentIndex}
          onClose={handleGalleryClose}
        />
      )}
    </>
  );
};

export default ImageCarousel;
